import { getDollarTokenAmount, tokenValuesQueryOptions } from "@/api/data";
import { myWalletQueryOptions } from "@/api/me";
import { api } from "@/api/utils";
import { PageInset } from "@/components/PageInset";
import { TokenAmount } from "@/components/TokenAmount";
import { Button } from "@/components/ui/Button";
import { TopNavigation, TopNavigationBackButton } from "@/components/ui/TopNavigation";
import { TopupTermsModal } from "@/pages/(app)/top-up/TopupTermsModal";
import { formatCents } from "@common/utils/i18n";
import { Box, Checkbox, Field, Flex, Input, Label, Spinner, Table, Tbody, Text, Tr } from "@givenwell/components";
import { useDebouncedCallback } from "@givenwell/hooks";
import { QuoteAmount } from "@givenwell/marketplace-api";
import { keyframes, styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, getRouteApi, useCanGoBack, useNavigate, useRouter } from "@tanstack/react-router";
import { ReactNode, useState } from "react";
import { fallback, finite, number, object, optional, pipe, toMaxValue, toMinValue } from "valibot";

/**
 * The maximum amount of points that can be purchased in a single transaction.
 * This is equivalent to $5,000 (in NZ/AU markets at least)
 */
const MAX_PURCHASE_AMOUNT = 25000;

export const Route = createFileRoute("/_auth/_app/top-up/")({
  validateSearch: object({
    tokens: fallback(optional(pipe(number(), finite(), toMinValue(1), toMaxValue(MAX_PURCHASE_AMOUNT))), undefined),
  }),
  component: TopUpPage,
});

const PRESET_TOP_UP_AMOUNTS = [250, 500, 1000];
const SPINNER_SIZE = 40;

type TopupAmounts = QuoteAmount;

function useTopupMutation() {
  return useMutation({
    mutationFn: async (args: { amount: number }) => {
      return await api.topup.startTopupPurchase({
        requestBody: { token_amount: args.amount },
      });
    },
  });
}

function useQuotesQuery(tokenAmounts: number[]) {
  return useQuery({
    queryKey: ["topup-quotes", ...tokenAmounts],
    queryFn: async () => {
      return await api.topup.quoteTokenCosts({
        tokenAmount: tokenAmounts.filter(Boolean),
      });
    },
  });
}

type Pages = "choose" | "fixed";

const route = getRouteApi("/_auth/_app/top-up/");

function TopUpPage() {
  const { tokens: initialTokens, "return-to": returnUrl } = route.useSearch();
  const [tokenAmount, setTokenAmount] = useState(initialTokens);

  const { data: presetQuotes } = useQuotesQuery(PRESET_TOP_UP_AMOUNTS);
  const { data: customQuotes } = useQuotesQuery([tokenAmount ?? 0]);

  const quotes = [...(presetQuotes?.quotes ?? []), ...(customQuotes?.quotes ?? [])];

  const topupAmount = quotes.find(q => q.token_amount === tokenAmount);

  const topupMut = useTopupMutation();
  const navigate = useNavigate();

  const page: Pages = initialTokens !== undefined ? "fixed" : "choose";

  function handlePurchaseClick() {
    if (!tokenAmount) return;

    if (!termsAccepted) {
      setTermsError(true);
      return;
    }

    navigate({
      to: "/top-up/checkout/$tokenAmount",
      params: { tokenAmount: tokenAmount.toString() },
      search: {
        "return-to": returnUrl,
      },
      replace: true,
    });
  }

  function handleTermsAccepted(accepted: boolean) {
    if (accepted) {
      setTermsError(false);
    }
    setTermsAccepted(accepted);
  }

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const purchaseDisabled = !topupAmount || topupAmount.token_amount <= 0;

  return (
    <Box css={{ display: "flex", flexDirection: "column", flex: "1 0 0px", pt: "var(--safe-area-inset-top)" }}>
      <Box css={{ width: "100%", maxW: 600 + 32, mx: "auto", mb: 16 }}>
        <Header />
      </Box>
      <Box css={{ flex: "1 0 0px", overflowY: "auto" }}>
        <PageInset>
          <Box css={{ width: "100%", maxW: 600, mx: "auto" }}>
            <>
              {page === "choose" && (
                <Box css={{ height: 400 }}>
                  <Text
                    css={{
                      fontScale: "3xl",
                      weight: 500,
                      mb: 16,
                      fontFamily: fonts.ftHabit,
                    }}
                  >
                    Select Top Up Amount
                  </Text>
                  <TopupAmountSelector quotes={quotes} tokenAmount={tokenAmount} onTokenAmountChange={setTokenAmount} />
                </Box>
              )}
              {page === "fixed" && (
                <Box css={{ height: 400 }}>
                  <FixedTopupSummary amount={topupAmount} />
                </Box>
              )}
            </>
          </Box>
        </PageInset>
        <PersistentBottomSheet estimatedHeight={100}>
          <TermsOfTopupCheckbox
            accepted={termsAccepted}
            highlighted={termsError}
            onAcceptedChanged={handleTermsAccepted}
          />
          <Button
            variant="filled"
            color="primary"
            size="lg"
            css={{ width: "100%", mt: 16 }}
            disabled={purchaseDisabled}
            onClick={handlePurchaseClick}
            loading={topupMut.isPending}
            loadingText="Processing..."
          >
            Continue to Payment
          </Button>
        </PersistentBottomSheet>
      </Box>
    </Box>
  );
}

function Header() {
  const canGoBack = useCanGoBack();
  const router = useRouter();
  const navigate = useNavigate();

  return (
    <TopNavigation
      title="Top Up Your Wallet"
      left={
        <TopNavigationBackButton
          onClick={e => {
            if (canGoBack) {
              e.preventDefault();
              router.history.back();
            } else {
              navigate({ to: "/wallet", replace: true });
            }
          }}
        />
      }
    />
  );
}

type PaymentSummaryProps = {
  amounts: TopupAmounts | undefined;
};
function PaymentSummary({ amounts }: PaymentSummaryProps) {
  if (!amounts) {
    return (
      <Table>
        <Tbody>
          <Tr>
            <Td css={{ width: "100%" }}>Top up amount</Td>
          </Tr>
          <Tr>
            <Td css={{ width: "100%" }}>Subtotal</Td>
          </Tr>
          <Tr>
            <Td css={{ width: "100%" }}>Card fee</Td>
          </Tr>
          <Tr>
            <Td css={{ width: "100%" }}>Includes GST of</Td>
          </Tr>
          <Tr css={{ fontWeight: "bold" }}>
            <Td css={{ width: "100%" }}>Total</Td>
          </Tr>
        </Tbody>
      </Table>
    );
  }

  return (
    <Box css={{ backgroundColor: "#CCE8E7", p: 16, borderRadius: 12 }}>
      <Table>
        <Tbody>
          <Tr>
            <Td
              colSpan={2}
              css={{
                width: "100%",
                fontScale: "lg",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              <TokenAmount amount={amounts.token_amount} withText />
            </Td>
          </Tr>
          <Tr>
            <Td css={{ width: "100%" }}>Subtotal</Td>
            <Td css={{ align: "right" }}>
              {formatCents(amounts.purchase_price - amounts.payment_fee_amount, amounts.currency, true)}
            </Td>
          </Tr>
          <Tr>
            <Td css={{ width: "100%" }}>Card fee</Td>
            <Td css={{ align: "right" }}>{formatCents(amounts.payment_fee_amount, amounts.currency, true)}</Td>
          </Tr>
          <Tr>
            <Td css={{ width: "100%" }}>Includes GST of</Td>
            <Td css={{ align: "right" }}>{formatCents(amounts.tax_amount, amounts.currency, true)}</Td>
          </Tr>
          <Tr css={{ fontWeight: "bold" }}>
            <Td css={{ width: "100%" }}>Total</Td>
            <Td css={{ align: "right", whiteSpace: "nowrap" }}>
              {amounts.currency.toUpperCase()} {formatCents(amounts.purchase_price, amounts.currency, false)}
            </Td>
          </Tr>
        </Tbody>
      </Table>
    </Box>
  );
}

type TopupAmountSelectorProps = {
  tokenAmount: number | undefined;
  onTokenAmountChange: (tokenAmount: number | undefined) => void;
  quotes: QuoteAmount[];
};
function TopupAmountSelector({ quotes, tokenAmount, onTokenAmountChange }: TopupAmountSelectorProps) {
  const [isCustomAmountSelected, setIsCustomAmountSelected] = useState(false);

  function handlePresetSelected(tokenAmount: number) {
    onTokenAmountChange(tokenAmount);
    setIsCustomAmountSelected(false);
  }

  function handleCustomAmountSelected(tokenAmount: number | undefined) {
    setIsCustomAmountSelected(true);
    onTokenAmountChange(tokenAmount);
  }

  function handleCustomAmountChanged(amount: number | undefined) {
    if (amount === undefined) {
      onTokenAmountChange(undefined);
    } else {
      onTokenAmountChange(amount);
    }
  }

  return (
    <Flex css={{ flexDir: "column", gap: 12, height: 300 }}>
      {PRESET_TOP_UP_AMOUNTS.map(amount => {
        const quote = quotes.find(q => q.token_amount === amount);
        if (!quote) return null;
        return (
          <TopupAmountOption
            key={amount}
            quote={quote}
            selected={tokenAmount === quote.token_amount && !isCustomAmountSelected}
            onSelect={() => handlePresetSelected(amount)}
          />
        );
      })}
      <TopupCustomAmountOption
        selected={isCustomAmountSelected}
        onSelect={handleCustomAmountSelected}
        onTokenAmountChange={handleCustomAmountChanged}
        tokenAmount={tokenAmount}
        quotes={quotes}
      />
    </Flex>
  );
}

type TopupAmountOptionProps = {
  quote: QuoteAmount;
  selected: boolean;
  onSelect: () => void;
};
function TopupAmountOption({ quote, selected, onSelect }: TopupAmountOptionProps) {
  return (
    <Box
      onClick={onSelect}
      css={{
        p: "12px 16px",
        backgroundColor: selected ? colors.navy : colors.cyanLight,
        color: selected ? colors.white : colors.black,
        fontWeight: "medium",
        borderRadius: 8,
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <TokenAmount
        amount={quote.token_amount}
        size="md"
        withText
        primaryColor={selected ? colors.white : colors.navy}
        secondaryColor={selected ? colors.white : colors.text2}
        css={{ fontWeight: 500 }}
      />
      <Text css={{ fontScale: "sm", color: selected ? colors.white : colors.text2 }}>
        {formatCents(quote.purchase_price, quote.currency)}
      </Text>
    </Box>
  );
}

type TopupCustomAmountOptionProps = {
  selected: boolean;
  onSelect: (tokenAmount: number | undefined) => void;
  onTokenAmountChange: (tokenAmount: number | undefined) => void;
  tokenAmount: number | undefined;
  quotes: QuoteAmount[];
};
function TopupCustomAmountOption({
  tokenAmount,
  selected,
  onSelect,
  onTokenAmountChange,
  quotes,
}: TopupCustomAmountOptionProps) {
  const [value, setValue] = useState(tokenAmount?.toString() || "");
  const quote = quotes.find(q => q.token_amount === tokenAmount);

  const walletQuery = useQuery(myWalletQueryOptions);
  const tokenValuesQuery = useQuery(tokenValuesQueryOptions);
  const tokenCurrency = walletQuery.data?.token_currency;
  const tokenValues = tokenValuesQuery.data;

  const debouncedCommit = useDebouncedCallback(handleValueCommit, 750);

  function handleValueChange(value: string) {
    setValue(value);
    debouncedCommit(value);
  }

  function getTokenAmountFromValue(value: string) {
    const points = parseInt(value, 10);
    if (isFinite(points) && tokenCurrency) {
      return getDollarTokenAmount(points, tokenCurrency, tokenValues);
    }
    return undefined;
  }

  function handleValueCommit(explicitValue?: string) {
    onTokenAmountChange(getTokenAmountFromValue(explicitValue ?? value));
  }

  return (
    <Box
      onClick={() => onSelect(getTokenAmountFromValue(value))}
      css={{
        p: "12px 16px",
        backgroundColor: selected ? colors.navy : colors.cyanLight,
        color: selected ? colors.white : colors.black,
        borderRadius: 8,
        cursor: "pointer",
        display: "flex",
        flexDir: "column",
        alignItems: "flex-start",
      }}
    >
      {!selected && <Text css={{ fontScale: "md", weight: 500 }}>Custom amount</Text>}
      {selected && (
        <Flex
          css={{
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Flex css={{ alignItems: "center", gap: 4 }}>
            <Input
              css={{ width: 140, ml: -4, fontScale: "md", color: colors.white }}
              autoFocus
              placeholder="Enter amount"
              value={value}
              onValueChange={handleValueChange}
              onValueCommit={handleValueCommit}
            />
            <Text css={{ userSelect: "none" }}>points</Text>
          </Flex>
          {quote && (
            <Text
              css={{
                fontScale: "sm",
                color: selected ? colors.white : colors.text2,
              }}
            >
              {formatCents(quote.purchase_price, quote.currency)}
            </Text>
          )}
        </Flex>
      )}
    </Box>
  );
}

type TermsOfTopupCheckboxProps = {
  accepted: boolean;
  highlighted: boolean;
  onAcceptedChanged: (accepted: boolean) => void;
};
function TermsOfTopupCheckbox(props: TermsOfTopupCheckboxProps) {
  const [termsOfSaleOpen, setTermsOfSaleOpen] = useState(false);

  function handleTermsAccepted() {
    setTermsOfSaleOpen(false);
    props.onAcceptedChanged(true);
  }

  return (
    <Field
      css={{
        flexDir: "row",
        items: "center",
        color: props.highlighted ? colors.red500 : undefined,
        animation: props.highlighted ? `${wiggle()} 0.5s` : "none",
      }}
    >
      <Checkbox checked={props.accepted} onCheckedChange={props.onAcceptedChanged} invalid={props.highlighted} />
      <Label>
        <Text css={{ fontScale: "xs" }}>
          I accept the&nbsp;
          <Text as="span" css={{ textDecoration: "underline" }}>
            <a href="#" onClick={() => setTermsOfSaleOpen(true)}>
              Terms of Wallet Top-Up
            </a>
          </Text>
        </Text>
      </Label>
      <TopupTermsModal open={termsOfSaleOpen} onAccept={handleTermsAccepted} onOpenChange={setTermsOfSaleOpen} />
    </Field>
  );
}

function FixedTopupSummary({ amount }: { amount: TopupAmounts | undefined }) {
  return (
    <Box>
      {!amount && (
        <Flex
          css={{
            alignItems: "center",
            justifyContent: "center",
            height: 240,
            mb: SPINNER_SIZE,
          }}
        >
          <Spinner css={{ size: SPINNER_SIZE }} />
        </Flex>
      )}
      {amount && (
        <>
          <PaymentSummary amounts={amount} />
        </>
      )}
    </Box>
  );
}

const wiggle = keyframes({
  "0%, 100%": {
    transform: "rotate(0deg)",
  },
  "25%, 75%": {
    transform: "rotate(-2.5deg)",
  },
  "50%": {
    transform: "rotate(2.5deg)",
  },
});

const Td = styled("td", {
  textAlign: "left",
  fontScale: "sm",
  py: 4,
  px: 8,
  "&:where(:first-child)": { pl: 16 },
  "&:last-child": { pr: 16 },
});

function PersistentBottomSheet({ children, estimatedHeight }: { children?: ReactNode; estimatedHeight: number }) {
  return (
    <>
      <Box style={{ height: estimatedHeight }} />
      <Flex
        css={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          justify: "center",
        }}
      >
        <Box
          css={{
            width: "100%",
            backgroundColor: colors.cream,
            padding: 20,
            pb: "max(var(--safe-area-inset-bottom), 20px)",
            boxShadow: "0px 0px 4px 4px rgba(0, 0, 0, 0.04)",

            "@sm": {
              roundedT: 16,
              maxW: 375,
            },
          }}
        >
          {children}
        </Box>
      </Flex>
    </>
  );
}
